/**
 * Extraction Preview — shows extracted event data in an editable form.
 *
 * Renders after MagicImportInput completes extraction.
 * User can review, edit any field, then confirm to proceed.
 */
import { useState } from 'react';
import {
  Alert, Box, Button, Chip, Divider, IconButton,
  TextField, Typography,
} from '@mui/material';
import { Check, Edit, Refresh } from '@mui/icons-material';
import type { EventSiteData, ScheduleItem, FAQItem } from '../types/event-data';

interface Props {
  data: EventSiteData;
  extractionId: string;
  onConfirm: (editedData: EventSiteData) => void;
  onRetry: () => void;
}

function ConfidenceBadge({ score }: { score: number }) {
  if (score >= 0.7) return <Chip label="High confidence" color="success" size="small" variant="outlined" />;
  if (score >= 0.4) return <Chip label="Partial — please review" color="warning" size="small" variant="outlined" />;
  return <Chip label="Low — please verify" color="error" size="small" variant="outlined" />;
}

export default function ExtractionPreview({ data, extractionId, onConfirm, onRetry }: Props) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<EventSiteData>({ ...data });

  const update = (field: keyof EventSiteData, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const filledCount = [
    form.event_name, form.event_date, form.venue_name, form.venue_city,
    form.description, form.hosts?.length, form.schedule?.length, form.faq?.length,
  ].filter(Boolean).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Check sx={{ color: 'success.main' }} />
          <Typography variant="h6" fontWeight={600}>
            We found your event details
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ConfidenceBadge score={data.confidence_score ?? 0} />
          <IconButton size="small" onClick={() => setEditing(!editing)} title={editing ? 'Done editing' : 'Edit all'}>
            <Edit fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {(data.confidence_score ?? 0) < 0.5 && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          We couldn't extract everything — please fill in the missing details below.
        </Alert>
      )}

      {/* Main fields */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Field label="Event Name" value={form.event_name} editing={editing}
          onChange={(v) => update('event_name', v)} />
        <Field label="Event Type" value={form.event_type} editing={editing}
          onChange={(v) => update('event_type', v)} />
        <Field label="Date" value={form.event_date} editing={editing}
          onChange={(v) => update('event_date', v)} type="date" />
        <Field label="End Date" value={form.event_end_date} editing={editing}
          onChange={(v) => update('event_end_date', v)} type="date" />
        <Field label="Venue" value={form.venue_name} editing={editing}
          onChange={(v) => update('venue_name', v)} />
        <Field label="City" value={form.venue_city} editing={editing}
          onChange={(v) => update('venue_city', v)} />
      </Box>

      <Field label="Address" value={form.venue_address} editing={editing} fullWidth
        onChange={(v) => update('venue_address', v)} />
      <Field label="Description" value={form.description} editing={editing} fullWidth multiline
        onChange={(v) => update('description', v)} />

      {/* Hosts */}
      {(form.hosts?.length ?? 0) > 0 && (
        <Box>
          <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>Hosts</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {form.hosts!.map((h, i) => <Chip key={i} label={h} size="small" />)}
          </Box>
        </Box>
      )}

      {/* Summary chips for nested data */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {(form.schedule?.length ?? 0) > 0 && (
          <Chip label={`Schedule (${form.schedule!.length} items)`} size="small" variant="outlined" />
        )}
        {(form.faq?.length ?? 0) > 0 && (
          <Chip label={`FAQ (${form.faq!.length} items)`} size="small" variant="outlined" />
        )}
        {(form.accommodation?.length ?? 0) > 0 && (
          <Chip label={`Hotels (${form.accommodation!.length})`} size="small" variant="outlined" />
        )}
        {form.dress_code && (
          <Chip label={`Dress code: ${form.dress_code}`} size="small" variant="outlined" />
        )}
      </Box>

      <Divider />

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
        <Button
          variant="text"
          startIcon={<Refresh />}
          onClick={onRetry}
          sx={{ color: 'text.secondary' }}
        >
          Try another URL
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => onConfirm(form)}
          sx={{ borderRadius: 2, px: 4 }}
        >
          Looks good — Continue
        </Button>
      </Box>

      <Typography variant="caption" color="text.secondary" textAlign="center">
        {filledCount}/8 fields extracted — you can edit anything above or add more details later
      </Typography>
    </Box>
  );
}


function Field({
  label, value, editing, onChange, fullWidth, multiline, type,
}: {
  label: string;
  value?: string | null;
  editing: boolean;
  onChange: (value: string) => void;
  fullWidth?: boolean;
  multiline?: boolean;
  type?: string;
}) {
  if (editing || !value) {
    return (
      <TextField
        label={label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        fullWidth={fullWidth}
        multiline={multiline}
        rows={multiline ? 2 : undefined}
        type={type}
        size="small"
        placeholder={value ? undefined : `Add ${label.toLowerCase()}`}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
        sx={{
          ...(!value && {
            '& .MuiOutlinedInput-root': {
              borderStyle: 'dashed',
              borderColor: 'grey.300',
            },
          }),
        }}
      />
    );
  }

  return (
    <Box sx={{ gridColumn: fullWidth ? '1 / -1' : undefined }}>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  );
}
